import { computed, ref, watch } from 'vue';
import { logError } from '@/common/helpers';
import { logDEBUG } from '@/common/debug';
import {
  BALANCE_CACHE_DURATION_MS,
  BALANCE_FETCH_RETRY_DELAYS_MS,
  BALANCE_REFRESH_DELAY_MS,
  BALANCE_REFRESH_DEBOUNCE_MS,
} from '@/common/config';
import useAccounts from '@/composables/accounts/useAccounts';
import { getTokenBalanceAndAllowance } from '@/common/predictionMarkets/predictionMarkets';
import { useBalanceRefresh } from '@/composables/useBalanceRefresh';
import { useAllowanceRefresh } from '@/composables/useAllowanceRefresh';
import { useBlockchainEvents } from '@/composables/useBlockchainEvents';

/**
 * Composable for managing token balance, native balance, and token allowance with batched fetching
 * Uses Multicall3 to fetch all three values in a single RPC call for maximum efficiency
 * Reduces from 3 RPC calls to 1 RPC call
 * Includes WebSocket subscriptions for instant balance updates on blockchain events
 * Cache persists across component mounts/unmounts
 */

// Singleton state - persists across component mounts
const tokenBalance = ref<bigint | null>(null);
const nativeBalance = ref<bigint | null>(null);
const allowance = ref<bigint | null>(null);
const lastFetchTime = ref<number>(0);
const lastFetchedAddress = ref<string | null>(null);
const isFetching = ref(false);
let currentFetchPromise: Promise<void> | null = null;

// Track if watchers are already set up (singleton watchers)
let watchersInitialized = false;

// WebSocket subscription instance (singleton)
let blockchainEventsInstance: ReturnType<typeof useBlockchainEvents> | null = null;

// Debounce timer for refresh triggers (singleton)
let refreshDebounceTimer: number | null = null;

export function useBalanceAndAllowance() {
  const accounts = useAccounts();
  const { refreshTrigger: balanceRefreshTrigger } = useBalanceRefresh();
  const { refreshTrigger: allowanceRefreshTrigger } = useAllowanceRefresh();

  // Initialize WebSocket subscriptions if not already done
  if (!blockchainEventsInstance) {
    blockchainEventsInstance = useBlockchainEvents();
  }

  async function fetchAll(force: boolean = false) {
    const currentAccount = accounts.selectedAccount.value;

    if (!currentAccount) {
      tokenBalance.value = null;
      nativeBalance.value = null;
      allowance.value = null;
      lastFetchTime.value = 0;
      lastFetchedAddress.value = null;
      return;
    }

    const userAddress = currentAccount.address;

    // If address changed, clear cache
    if (lastFetchedAddress.value !== userAddress) {
      lastFetchTime.value = 0;
      lastFetchedAddress.value = userAddress;
    }

    // Check cache unless forced
    const now = Date.now();
    if (!force && lastFetchTime.value > 0 && now - lastFetchTime.value < BALANCE_CACHE_DURATION_MS) {
      logDEBUG('fetchBalanceAndAllowance', userAddress, 'CACHED');
      return; // Use cached value
    }

    // If forced, clear cache timestamp to ensure fresh fetch
    if (force) {
      lastFetchTime.value = 0;
    }

    // Prevent concurrent fetches
    if (isFetching.value) {
      if (force && currentFetchPromise) {
        logDEBUG('fetchBalanceAndAllowance', userAddress, 'WAITING - fetch in progress, will retry after completion');
        // Wait for current fetch to complete, then retry forced fetch
        await currentFetchPromise;
        return await fetchAll(true);
      } else {
        logDEBUG('fetchBalanceAndAllowance', userAddress, 'SKIP - already fetching');
        return;
      }
    }

    // Create fetch promise that we can await if needed
    currentFetchPromise = (async () => {
      try {
        isFetching.value = true;
        logDEBUG('fetchBalanceAndAllowance', userAddress, force ? '(forced)' : '');

        // Fetch token balance + allowance + native balance in a single batched Multicall3 call
        // Uses the wrapper function which handles both EVM and play accounts
        const result = await getTokenBalanceAndAllowance(userAddress);

        tokenBalance.value = result.balance;
        allowance.value = result.allowance;
        nativeBalance.value = result.nativeBalance;
        lastFetchTime.value = Date.now(); // Set timestamp after fetch completes
      } catch (err) {
        logError('useBalanceAndAllowance', 'fetching balance and allowance', err);
      } finally {
        isFetching.value = false;
        currentFetchPromise = null;
      }
    })();

    await currentFetchPromise;
  }

  // Debounced fetch function to prevent rapid successive refreshes
  function debouncedFetchAll() {
    if (refreshDebounceTimer !== null) {
      clearTimeout(refreshDebounceTimer);
    }
    refreshDebounceTimer = window.setTimeout(() => {
      fetchAll(true);
      refreshDebounceTimer = null;
    }, BALANCE_REFRESH_DEBOUNCE_MS);
  }

  // Set up watchers only once (singleton pattern)
  if (!watchersInitialized) {
    watchersInitialized = true;

    // Watch for account changes
    watch(
      () => accounts.selectedAccount.value,
      (newAccount, oldAccount) => {
        // Only fetch if account actually changed
        if (newAccount?.address !== oldAccount?.address) {
          fetchAll(true); // Force refresh when switching accounts to ensure fresh data
        }
        // Note: WebSocket subscriptions are managed by App.vue
      },
      { immediate: true },
    );

    // Watch for balance refresh trigger (debounced to prevent duplicate fetches)
    watch(balanceRefreshTrigger, debouncedFetchAll);

    // Watch for allowance refresh trigger (debounced to prevent duplicate fetches)
    watch(allowanceRefreshTrigger, debouncedFetchAll);
  }

  /**
   * Wait for balance/allowance to update after a blockchain transaction
   * With WebSocket: relies on event-driven updates but does one safety fetch after a short delay
   * to handle RPC node caching (WebSocket event may fire before node state updates)
   * Without WebSocket: retries with longer delays
   * @param maxRetries Maximum number of retry attempts when WebSocket is not active (default: 3)
   */
  async function waitForBalanceUpdate(maxRetries: number = 3) {
    // Check if WebSocket subscriptions are active
    const isWebSocketActive = blockchainEventsInstance?.isSubscribed.value || false;

    if (isWebSocketActive) {
      // WebSocket events fire instantly, but RPC nodes may have cached state
      // Wait briefly for node to update, then do one fetch
      // This is much faster than the full retry mechanism (500ms vs 2.5s)
      logDEBUG('waitForBalanceUpdate', 'WebSocket active, brief delay + one fetch');
      await new Promise((resolve) => setTimeout(resolve, BALANCE_REFRESH_DELAY_MS));
      await fetchAll(true);
    } else {
      // No WebSocket, use full polling mechanism with retries
      logDEBUG('waitForBalanceUpdate', 'No WebSocket connected, using retry mechanism');
      let retries = 0;
      while (retries < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, BALANCE_FETCH_RETRY_DELAYS_MS[retries === 0 ? 0 : 1]));
        await fetchAll(true);
        retries++;
      }
    }
  }

  return {
    tokenBalance: computed(() => tokenBalance.value),
    nativeBalance: computed(() => nativeBalance.value),
    allowance: computed(() => allowance.value),
    isFetching: computed(() => isFetching.value),
    fetchAll,
    waitForBalanceUpdate,
    clearCache: () => {
      logDEBUG('[CACHE CLEAR]', 'Balance and allowance cache');
      lastFetchTime.value = 0;
    },
  };
}
